import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import { useDispatch, useSelector } from "react-redux";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import {
  BILLER_PROFILE_DETAILS,
  BILLER_IMAGE_URL,
} from "../../constants/BillerManagementConstant";
import { getFinancialInstitutionHeadings } from "../../constants/FinancialInstitutionConstant";
import i18next from "i18next";
import {
  getNextFilterId,
  convertDateStringIntoERPSDateFormat,
  getLabelName,
} from "../../utils/helper";
import ERPSBaseCheckBoxGrp from "../custom/inputs/ERPSBaseCheckBoxGrp";
import actions from "../../actions";
import ErrorMessage from "../custom/containers/ErrorMessage";
import { CircularProgress, Grid, HStack, ResponsiveBox, Stack, Typography } from "../primitives";

const UpdateBillerApproval = (props) => {
  const { t } = useTranslation();
  const loginUser = useSelector((state) => state.loginUserReducer?.user);
  const selectedFi = useSelector((state) => state.fiReducer?.selectedFi);
  const fiNum = selectedFi || loginUser.fiNumber;

  const errorMessage = useSelector(
    (state) => state.billerApprovalReducer?.billerActionError
  );

  const {
    billerData: { data, updatedData },
    componentAccess,
    billerPendingData,
    userId: loggedInUser,
  } = props;
  const dispatch = useDispatch();
  const [updateLoading, setUpdateLoading] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [lastRecordDisable, setLastRecordDisable] = useState(false);
  const errorRef = useRef();

  const [financialInsititutionData, setFinancialInstitutionTableList] =
    useState([]);

  const userId = useSelector((state) => state.loginUserReducer?.user?.userId);

  useEffect(() => {
    if (data?.billerProfileExtensions) {
      const billerFiUpdatedData = updatedData?.billerProfileExtensions.map(
        (prevData, idx) => {
          const matchedData = data?.billerProfileExtensions?.find(
            (updatedData) => updatedData?.fiNumber == prevData?.fiNumber
          );
          if (
            matchedData &&
            prevData?.billerStatus !== matchedData?.billerStatus
          ) {
            return {
              ...prevData,
              id: idx,
              ...(prevData?.billerStatus !== matchedData?.billerStatus && {
                updatedStatus: matchedData?.billerStatus,
              }),
            };
          }
          // return { ...prevData, id: idx };
        }
      );
      setFinancialInstitutionTableList(billerFiUpdatedData.filter((e) => e));
    }
  }, [data?.billerProfileExtensions]);

  useEffect(() => {
    if (errorRef.current) {
      errorRef.current.focus();
    }
  }, [errorMessage]);

  const bankDetailKeyLabel = {
    creditorFiNumber: t("erps.billermanagement.label.billerFinancialInstitution", "Biller financial institution"),
    creditorTransitNumber: t("erps.billermanagement.label.transitNumber", "Transit number"),
    creditorAccountNumber: t("erps.billermanagement.label.accountNumber", "Account number"),
  };

  const getAccontSettingData = ({ data, updatedData, allowedKeys }) => {
    let obj = [];
    for (let key in data) {
      if (allowedKeys.includes(key)) {
        const updatedKeyValue = bankDetailKeyLabel[key];
        if (data[key] !== updatedData?.[key]) {
          obj.push({
            id: key,
            attribute: updatedKeyValue,
            currentValue: updatedData?.[key],
            updatedValue: data?.[key],
          });
        }
        // else {
        //   obj.push({
        //     id: key,
        //     attribute: updatedKeyValue,
        //     currentValue: updatedData?.[key],
        //     updatedValue: "",
        //   });
        // }
      }
    }
    return obj;
  };

  const getImageUrlData = () => {
    let obj = [];
    if (
      data?.billerMasterRules?.masterRules?.image_url !=
      updatedData?.billerMasterRules?.masterRules?.image_url
    ) {
      obj.push({
        id: 1,
        currentValue: updatedData?.billerMasterRules?.masterRules?.image_url,
        updatedValue: data?.billerMasterRules?.masterRules?.image_url,
      });
    }
    return obj;
  };

  const navigateToUserPendingApporvalPage = () => {
    props.history.push("/biller-pending-approvals", { componentAccess, fromDetailPage: true });
  };

  const hasLastActiveRecord = () => {
    return (billerPendingData && billerPendingData.length === 1);
  };

  const handleAction = (event, isSubmit) => {
    const setLoadingFunction = isSubmit ? setUpdateLoading : setDeclineLoading;
    setLoadingFunction(true);
    if (event) event.preventDefault();
    const { id, fiNumber, billerId } = data;

    let request = {
      transactionId: id,
      userId,
      fiNumber: fiNum,
      billerId,
      languageCode: i18next.language === "en" ? "E" : "F",
    };
    if (isSubmit)
      request.actionCode = "U";

    const filteredQueueDataList = billerPendingData.filter(
      ({ id: billerId }) => billerId != id
    );

    const currentIndexVal = billerPendingData.findIndex(
      ({ id: billerId }) => billerId == id
    );
    const slicedArr = billerPendingData.slice(currentIndexVal + 1);
    const requiredArr =
      slicedArr && slicedArr.length ? slicedArr : filteredQueueDataList;

    const nextTransactionId = getNextFilterId(
      filteredQueueDataList,
      requiredArr,
      loggedInUser,
      "id"
    );
    const hasLastRecord = hasLastActiveRecord();
    if (props?.setShowMessage) {
      props?.setShowMessage(false);
    }

    const svcProps = {
      ...props,
      setLoading: setLoadingFunction,
      t,
      nextTransactionId,
      queueDataList:
        nextTransactionId === ""
          ? billerPendingData
          : filteredQueueDataList,
      componentAccess,
      setLastRecordDisable,
      hasLastRecord,
    };

    dispatch(
      isSubmit
        ? actions.billerPendingApprovalsActions.updateBillerApprovalData(request, svcProps)
        : actions.billerPendingApprovalsActions.declineBillerApprovalData(request, svcProps)
    );
  };

  const billerPendingApprovalChildAccess = componentAccess.find(
    ({ componentType }) => componentType === "BPA"
  );
  const billerApproverComponentAccess =
    billerPendingApprovalChildAccess?.childComponents;

  const contactInfo = [
    {
      id: 0,
      attribute: "Primary contact",
      value: data?.primaryContactName,
    },
    {
      id: 1,
      attribute: "Primary fax number",
      value: data?.primaryContactFax,
    },
    {
      id: 2,
      attribute: "Secondary telephone number and extension",
      value:
        data?.secondaryContactExtension + ":" + data?.secondaryContactPhone,
    },
  ];

  return (
    <>
      <ErrorMessage 
        error={errorMessage}
        errorRef={errorRef}
        mt={4}
      />
      <Grid container>
        <Grid item lg={8} sm={12}>
          <ResponsiveBox className="manageUsers__page__title">
            <Typography variant="h1">
              {t("erps.billermanagement.pendingBiller.heading", {
                transactionId: data.id,
                billerId: data.billerId,
              })}
            </Typography>
          </ResponsiveBox>
          <ResponsiveBox mt={4}>
            <Typography variant="h2">
              {t("erps.updatebillerapproval.heading.level2.approvaldetails")}
            </Typography>
          </ResponsiveBox>
          <ResponsiveBox mt={3} sx={{ display: "flex" }}>
            <Typography className="available__documents__value">
              {t("erps.updatebillerapproval.label.updatedby")}
              <Typography variant="body2">{data?.displayUpdatedBy || data?.updatedBy}</Typography>
            </Typography>
            <Typography className="available__documents__value">
              {t("erps.updatebillerapproval.label.updatedon")}
              <Typography variant="body2">
                {convertDateStringIntoERPSDateFormat(data.updatedDate)}
              </Typography>
            </Typography>
            <Typography className="available__documents__value">
              {t("erps.updatebillerapproval.label.action")}
              <Typography variant="body2">{"Update"}</Typography>
            </Typography>
          </ResponsiveBox>
          <ResponsiveBox mt={4}>
            <Typography variant="h2">
              {t("erps.updatebillerapproval.heading.level2.userprofile")}
            </Typography>
            <ResponsiveBox>
              <p>{t("erps.updatebillerapproval.userprofile.description")}</p>
            </ResponsiveBox>
          </ResponsiveBox>
          {/* Bank details */}
          {getAccontSettingData({
            data,
            allowedKeys: [
              "creditorFiNumber",
              "creditorTransitNumber",
              "creditorAccountNumber",
            ],
            updatedData,
          })?.length ? (
            <>
              <ResponsiveBox mt={4}>
                <Typography variant="h2" id="bankAccount">
                  {t("erps.billermanagement.label.bankAccount", "Bank account")}
                </Typography>
              </ResponsiveBox>
              <ResponsiveBox mt={2}>
                <ResponsiveDataGrid
                  data={{
                    rows:
                      getAccontSettingData({
                        data,
                        allowedKeys: [
                          "creditorFiNumber",
                          "creditorTransitNumber",
                          "creditorAccountNumber",
                        ],
                        updatedData,
                      }) || [],
                    count: contactInfo.length || "0",
                    columns: BILLER_PROFILE_DETAILS,
                  }}
                  dataGridWidth="dataGrid__box__container__billerProfileData_update"
                  hideFooterPagination={true}
                  getCellClassName={(params) => {
                    if (params.field === "currentValue") {
                      return "column_separator_border-right";
                    }
                  }}
                  tableLabelText={t(
                    "erps.billermanagement.label.bankAccount",
                    "Bank account"
                  )}
                />
              </ResponsiveBox>
            </>
          ) : null}
          {/* Financial institution table */}
          {financialInsititutionData?.length ? (
            <ResponsiveBox mt={4}>
              <Typography variant="h2">
                {t("erps.updatebillerapproval.heading.financialInstitution")}
              </Typography>
              <ResponsiveBox>
                <p>
                  {t(
                    "erps.updatebillerapproval.heading.financialInstitution.description"
                  )}
                </p>
              </ResponsiveBox>
              <ResponsiveDataGrid
                data={{
                  rows: financialInsititutionData,
                  count: financialInsititutionData.length,
                  columns: getFinancialInstitutionHeadings(
                    false,
                    "updateApprovalPage"
                  ),
                }}
                dataGridWidth="dataGrid__box__container__billerProfileData_update"
                hideFooterPagination={true}
                getCellClassName={(params) => {
                  if (
                    params.field === "fiNumber" ||
                    params.field === "fiName" ||
                    params.field === "updatedStatusCurrent"
                  ) {
                    return "column_separator_border-right-only";
                  }
                }}
                tableLabelText={t(
                  "erps.updatebillerapproval.heading.financialInstitution.description"
                )}
              />
            </ResponsiveBox>
          ) : null}
          {/* English Image url */}
          {getImageUrlData().length ? (
            <ResponsiveBox mt={4}>
              <Typography variant="h4">
                {t("erps.updatebillerapproval.imageinstructiontext.label.englishimageurl")}
              </Typography>
              <ResponsiveBox class="image__url__table">
                <ResponsiveDataGrid
                  data={{
                    rows: getImageUrlData() || [],
                    count: getImageUrlData().length || "0",
                    columns: BILLER_IMAGE_URL,
                  }}
                  dataGridWidth="dataGrid__box__container__billerProfileData_update"
                  hideFooterPagination={true}
                  autoRowHeight={true}
                  getCellClassName={(params) => {
                    if (params.field === "currentValue") {
                      return "column_separator_border-right-only";
                    }
                  }}
                  tableLabelText={t(
                    "erps.updatebillerapproval.imageinstructiontext.label.englishimageurl"
                  )}
                />
              </ResponsiveBox>
            </ResponsiveBox>
          ) : null}

          {fiNum == "10" && (
            <>
              {" "}
              <ResponsiveBox className="manageUsers__page__title">
                {
                  <>
                    {" "}
                    <ResponsiveBox className="manageUsers__page__title">
                      <Typography variant="h2">
                        {t("erps.billermanagement.label.lineofbusiness", "Line of business settings")}
                      </Typography>
                    </ResponsiveBox>
                    <ResponsiveBox mt={3}>
                      <Typography variant="h4">
                        {t("erps.billermanagement.label.image", "Image")}
                      </Typography>
                    </ResponsiveBox>
                    <ResponsiveBox style={{ width: "500px" }}>
                      <ResponsiveBox
                        mt={3}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <ResponsiveBox>
                          <Typography>
                            {t("erps.billermanagement.label.cibc", "CIBC:")}
                          </Typography>
                          <br />
                          <ResponsiveBox mt={-3}>
                            <ResponsiveBox>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcTelphoneBankingIVR",
                                  "Telephone banking IVR"
                                )}
                                id="imageCibcTelephoneBankingIVR"
                                value={ (data?.lineOfbusinessData?.isImgCIBCTelephoneBankingIVRChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                            <ResponsiveBox mt={-1}>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcBranchBanking",
                                  "Branch banking"
                                )}
                                id="imageCibcBranchBanking"
                                value={ (data?.lineOfbusinessData?.isImgCIBCBranchBankingChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                            <ResponsiveBox mt={-1}>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcTelphoneBankingDesk",
                                  "Telephone banking (Desktop)"
                                )}
                                id="imageCibcTelephoneBankingDesktop"
                                value={ (data?.lineOfbusinessData?.isImgCIBCTelePhoneBankingDeskChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                            <ResponsiveBox mt={-1}>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcOnlineBanking",
                                  "Online banking"
                                )}
                                id="imageCibcOnlineBanking"
                                value={ (data?.lineOfbusinessData?.isImgCIBCOnlineBankingChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                          </ResponsiveBox>
                        </ResponsiveBox>
                        <ResponsiveBox>
                          <Typography>
                            {t(
                              "erps.billermanagement.label.pcfinancial",
                              "PC Financial:"
                            )}
                          </Typography>
                          <br />
                          <ResponsiveBox mt={-3}>
                            <ResponsiveBox>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcTelphoneBankingIVR",
                                  "Telephone banking IVR"
                                )}
                                id="imagePcFinanceTelephoneBankingIVR"
                                value={ (data?.lineOfbusinessData?.isImgPCFinancialTelephoneBankingIVRChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>

                            <ResponsiveBox mt={3}>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcTelphoneBankingDesk",
                                  "Telephone banking (Desktop)"
                                )}
                                id="imagePcFinanceTelephoneBankingDesktop"
                                value={ (data?.lineOfbusinessData?.isImgPCFinancialTelePhoneBankingDeskChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                            <ResponsiveBox mt={-1}>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcOnlineBanking",
                                  "Online banking"
                                )}
                                id="imagePcFinanceOnlineBanking"
                                value={ (data?.lineOfbusinessData?.isImgPCFinancialOnlineBankingChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                          </ResponsiveBox>
                        </ResponsiveBox>
                      </ResponsiveBox>
                    </ResponsiveBox>
                    <ResponsiveBox mt={3}>
                      <Typography variant="h4">
                        {t(
                          "erps.billermanagement.label.instructional",
                          "Instructional"
                        )}
                      </Typography>
                    </ResponsiveBox>
                    <ResponsiveBox style={{ width: "500px" }}>
                      <ResponsiveBox
                        mt={3}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <ResponsiveBox>
                          <Typography>
                            {t("erps.billermanagement.label.cibc", "CIBC:")}
                          </Typography>
                          <br />
                          <ResponsiveBox mt={-3}>
                            <ResponsiveBox>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcTelphoneBankingIVR",
                                  "Telephone banking IVR"
                                )}
                                id="instructionalCibcTelephoneBankingIVR"
                                value={ (data?.lineOfbusinessData?.isInstCIBCTelephoneBankingIVRChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                            <ResponsiveBox mt={-1}>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcBranchBanking",
                                  "Branch banking"
                                )}
                                id="instructionalCibcBranchBanking"
                                value={ (data?.lineOfbusinessData?.isInstCIBCBranchBankingChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                            <ResponsiveBox mt={-1}>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcTelphoneBankingDesk",
                                  "Telephone banking (Desktop)"
                                )}
                                id="instructionalCibcTelephoneBankingDesktop"
                                value={ (data?.lineOfbusinessData?.isInstCIBCTelePhoneBankingDeskChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                            <ResponsiveBox mt={-1}>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcOnlineBanking",
                                  "Online banking"
                                )}
                                id="instructionalCibcOnlineBanking"
                                value={ (data?.lineOfbusinessData?.isInstCIBCOnlineBankingChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                          </ResponsiveBox>
                        </ResponsiveBox>
                        <ResponsiveBox>
                          <Typography>
                            {t(
                              "erps.billermanagement.label.pcfinancial",
                              "PC Financial:"
                            )}
                          </Typography>
                          <br />
                          <ResponsiveBox mt={-3}>
                            <ResponsiveBox>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcTelphoneBankingIVR",
                                  "Telephone banking IVR"
                                )}
                                id="instructionalPcFinanceTelephoneBankingIVR"
                                value={ (data?.lineOfbusinessData?.isInstPCFinancialTelephoneBankingIVRChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>

                            <ResponsiveBox mt={3}>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcTelphoneBankingDesk",
                                  "Telephone banking (Desktop)"
                                )}
                                id="instructionalPcFinanceTelephoneBankingDesktop"
                                value={ (data?.lineOfbusinessData?.isInstPCFinancialTelePhoneBankingDeskChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                            <ResponsiveBox mt={-1}>
                              <ERPSBaseCheckBoxGrp
                                label={t(
                                  "erps.billermanagement.label.cibcOnlineBanking",
                                  "Online banking"
                                )}
                                id="instructionalPcFinanceOnlineBanking"
                                value={ (data?.lineOfbusinessData?.isInstPCFinancialOnlineBankingChecked === "Y") }
                                disabled={true}
                              />{" "}
                            </ResponsiveBox>
                          </ResponsiveBox>
                        </ResponsiveBox>
                      </ResponsiveBox>
                    </ResponsiveBox>
                  </>
                }
              </ResponsiveBox>
            </>
          )}

          <HStack spacing={2}>
            <ERPSBaseButton
              variant="secondary"
              className="button_with_margin48"
              label={t("erps.newuser.button.back", "Back")}
              onClick={navigateToUserPendingApporvalPage}
              componentAccess={billerApproverComponentAccess}
              actionType={"hasBillerPendingCancelAccess"}
            />
            <ERPSBaseButton
              functionType="NU"
              variant="secondary"
              onClick={(e) => handleAction(e, false)}
              loading={declineLoading}
              className="button_with_margin48"
              label={getLabelName({
                key: "decline",
                hasLastActiveRecord,
                lastRecordDisable,
                t,
              })}
              componentAccess={billerApproverComponentAccess}
              actionType={"hasBillerPendingDeclineAccess"}
              isDisabled={lastRecordDisable}
            />
            <ERPSBaseButton
              functionType="NU"
              variant="primary"
              onClick={(e) => handleAction(e, true)}
              loading={updateLoading}
              className="button_with_margin48"
              label={getLabelName({
                key: "approve",
                hasLastActiveRecord,
                lastRecordDisable,
                t,
              })}
              componentAccess={billerApproverComponentAccess}
              actionType={"hasBillerPendingApprovalAccess"}
              isDisabled={loggedInUser == data?.updatedBy || lastRecordDisable}
            />
          </HStack>
        </Grid>
      </Grid>
    </>
  );
};

export default UpdateBillerApproval;
